import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { Form, Formik, useFormikContext, type FormikHelpers } from "formik";
import moment from "moment/moment";
import { Tabs } from "../../UI/Tabs/Tabs";
import { IconTrash } from "@tabler/icons-react";
import CollapsibleCard from "../../UI/CollapsibleCard";
import Button from "../../UI/Button";
import FormikSubmitButton from "../../UI/FormikSubmitButton";
import Text from "../../UI/Text";
import Callout from "../../UI/Callout";
import Avatar from "../../UI/Avatar";
import IconButton from "../../UI/IconButton";
import ModalDialog from "../../UI/ModalDialog";
import Status from "../../UI/Status";
// @ts-expect-error pure js
import Checkbox from "../../UI/Checkbox";
// @ts-expect-error pure js
import RadioGroup from "../../UI/RadioGroup";
// @ts-expect-error pure js
import Radio from "../../UI/Radio";
// @ts-expect-error pure js
import TextInput from "../../UI/TextInput";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import useTenants from "../../../hooks/use-tenants";
import {
  useAllTenantsBalances,
  useTenantsBalanceQuittance,
  // @ts-expect-error pure js
} from "../../../hooks/use-balances";
// @ts-expect-error pure js
import useBalancesDashboard from "../../../hooks/use-balances-dashboard";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../api/Functions";
// @ts-expect-error pure js
import { handleAPIError } from "../../../utils";
// @ts-expect-error pure js
import { TenantStatus } from "../../../models/tenant_old";
import { getInitialsFromName } from "../../Profile/profileUtils";
import type { Tenant } from "../../../models/tenant";
import {
  getTenantPeriodLabel,
  getTenantPeriodMonths,
} from "../../../utils/balances";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../../UI/SelectStandalone";
import { useToast } from "../../UI/Toast/useToast";
import bulkStyles from "./BulkActions.module.css";
import styles from "./BulkQuittanceModal.module.css";
import BulkPdfPreview from "./BulkPdfPreview";
import BulkSelectionCard, { BulkSelectionCardRow } from "./BulkSelectionCard";
import { usePdfPreviewCache } from "./usePdfPreviewCache";
import type { AxiosInstance } from "axios";

type QuittanceType = "recu" | "quittance" | "facture_loyer" | "none";
type Periode = "period" | "date";

interface MonthStatus {
  type: QuittanceType;
  up_to_date: boolean;
}

type TenantQuittanceStatuses = Record<string, Record<string, MonthStatus>>;

interface QuittanceFile {
  id: number;
  filename: string;
  src: string;
  id_tenant: number;
}

interface BulkQuittanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTenantIds?: number[];
  resetSelection?: () => void;
  /** When set, the user can't add or remove tenants — the modal stays scoped to this tenant. */
  lockedTenantId?: number;
}

const MAX_MONTHS = 12;

function getDefaultDocType(tenant: Tenant | undefined): QuittanceType {
  return tenant?.is_type_quittance === false ? "facture_loyer" : "quittance";
}

function getDocTypeLabel(type: QuittanceType): string {
  switch (type) {
    case "quittance":
      return "Quittance";
    case "facture_loyer":
      return "Facture";
    case "recu":
      return "Reçu";
    case "none":
      return "Ne rien générer";
  }
}

function getTenantDocTypeLabel(tenant: Tenant | undefined): string {
  return tenant?.is_type_quittance === false ? "Facture" : "Quittance";
}

function getDocTypeFileLabel(type: QuittanceType): string {
  switch (type) {
    case "quittance":
      return "Quittance";
    case "facture_loyer":
      return "Facture";
    case "recu":
      return "Reçu";
    case "none":
      return "Document";
  }
}

function getLastPaymentDate(
  payments: { date_transac: string; montant: number }[] | undefined,
): string | undefined {
  if (!payments || payments.length === 0) return undefined;
  const latest = payments.reduce<string | undefined>((acc, p) => {
    if (acc === undefined) return p.date_transac;
    return moment(p.date_transac).isAfter(moment(acc)) ? p.date_transac : acc;
  }, undefined);
  return latest ? moment(latest).format("DD/MM/YYYY") : undefined;
}

function buildRecentMonths(count: number): string[] {
  const months: string[] = [];
  for (let i = 0; i < count; i++) {
    months.push(moment().subtract(i, "month").format("MM/YYYY"));
  }
  return months;
}

export default function BulkQuittanceModal({
  open,
  onOpenChange,
  selectedTenantIds = [],
  resetSelection,
  lockedTenantId,
}: BulkQuittanceModalProps) {
  const headerLabel = lockedTenantId
    ? "Générer une quittance / facture"
    : "Générer des quittances / factures";

  return (
    <ModalDialog
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      header={headerLabel}
    >
      <BulkQuittanceContent
        close={() => onOpenChange(false)}
        selectedTenantIds={selectedTenantIds}
        resetSelection={resetSelection}
        lockedTenantId={lockedTenantId}
      />
    </ModalDialog>
  );
}

interface BulkQuittanceContentProps {
  close: () => void;
  selectedTenantIds: number[];
  resetSelection?: () => void;
  lockedTenantId?: number;
}

interface FormValues {
  selectedMonths: string[];
  fromDate: string;
  tenantIds: number[];
  /** Keyed by `${tenantId}|${month}` — flat keys so they can be used as Formik field paths. */
  perTenantOverrides: Record<string, QuittanceType>;
  sendEmail: boolean;
}

function overrideKey(tenantId: number, month: string): string {
  return `${tenantId}|${month}`;
}

function BulkQuittanceContent({
  close,
  selectedTenantIds,
  resetSelection,
  lockedTenantId,
}: BulkQuittanceContentProps) {
  const axios = useAuthenticatedAxios();
  const toast = useToast();
  const {
    tenants,
    tenantsById,
  }: { tenants: Tenant[]; tenantsById: Record<number, Tenant> } = useTenants({
    withById: true,
  });

  const { balanceQuittance, isLoading: isLoadingBalances } =
    useTenantsBalanceQuittance(MAX_MONTHS) as {
      balanceQuittance: Record<
        number,
        { month: string; balance: number }[] | undefined
      >;
      isLoading: boolean;
    };

  const initialTenantIds = useMemo(() => {
    if (lockedTenantId) return [lockedTenantId];
    return selectedTenantIds;
  }, [lockedTenantId, selectedTenantIds]);

  const initialMonths = useMemo(() => [moment().format("MM/YYYY")], []);

  const [periode, setPeriode] = useState<Periode>("period");

  async function handleSubmit(
    values: FormValues,
    { setSubmitting, setStatus }: FormikHelpers<FormValues>,
  ) {
    setStatus(undefined);
    try {
      const quittanceStatuses = buildQuittanceStatuses(
        values,
        periode,
        tenantsById,
        balanceQuittance,
      );
      const response = await axios.post("/tenants/quittance", {
        quittance_statuses: quittanceStatuses,
        send_email: values.sendEmail,
      });
      const { files, errors } = response.data as {
        files: QuittanceFile[];
        errors: string[];
      };
      const successByTenant = new Map<number, number>();
      files.forEach((file) => {
        successByTenant.set(
          file.id_tenant,
          (successByTenant.get(file.id_tenant) ?? 0) + 1,
        );
      });

      const tenantName = (tenantId: number) =>
        tenantsById[tenantId]?.nom ?? `Locataire ${tenantId}`;

      const description = (
        <div className={bulkStyles.toastDescription}>
          {files.length > 0 && (
            <div className={bulkStyles.toastSection}>
              <Text weight="bold" color="neutral">
                Document{files.length > 1 ? "s" : ""} généré
                {files.length > 1 ? "s" : ""}
                {values.sendEmail ? " et envoyé par mail" : ""}
              </Text>
              <ul className={bulkStyles.toastList}>
                {Array.from(successByTenant.entries()).map(([tid, count]) => (
                  <li key={tid}>
                    <Text as="span" variant="deci">
                      <Text as="span" variant="deci" weight="bold">
                        {tenantName(tid)}
                      </Text>{" "}
                      : {count} document{count > 1 ? "s" : ""}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {errors.length > 0 && (
            <div className={bulkStyles.toastSection}>
              <Text weight="bold">
                Document{errors.length > 1 ? "s" : ""} non généré
                {errors.length > 1 ? "s" : ""}
              </Text>
              <ul className={bulkStyles.toastList}>
                {errors.map((message) => (
                  <li key={message}>
                    <Text as="span" variant="deci">
                      {message}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );

      if (files.length === 0) {
        if (errors.length > 0) {
          toast.error({ title: "Aucun document généré", description });
        } else {
          toast.info({
            title: "Aucun nouveau document généré",
            description: (
              <Text variant="deci">
                Toutes les quittances pour cette période existent déjà ou aucun
                document n'était à générer.
              </Text>
            ),
          });
        }
      } else {
        toast.success({
          title: `${files.length} document${
            files.length > 1 ? "s" : ""
          } généré${files.length > 1 ? "s" : ""}`,
          description,
        });
      }
      resetSelection?.();
      close();
    } catch (e) {
      setStatus(handleAPIError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik<FormValues>
      initialValues={{
        selectedMonths: initialMonths,
        fromDate: "",
        tenantIds: initialTenantIds,
        perTenantOverrides: {},
        sendEmail: false,
      }}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <Form className={bulkStyles.body}>
        <BulkQuittanceFields
          tenants={tenants}
          tenantsById={tenantsById}
          balanceQuittance={balanceQuittance}
          isLoadingBalances={isLoadingBalances}
          close={close}
          lockedTenantId={lockedTenantId}
          periode={periode}
          setPeriode={setPeriode}
        />
      </Form>
    </Formik>
  );
}

function isMonthAjour(
  balances: { month: string; balance: number }[] | undefined,
  month: string,
): boolean {
  if (!balances) return true;
  const entry = balances.find((b) => b.month === month);
  return entry === undefined || entry.balance >= 0;
}

function getMonthDefaultType(
  tenant: Tenant | undefined,
  ajour: boolean,
): QuittanceType {
  return ajour ? getDefaultDocType(tenant) : "none";
}

function buildQuittanceStatuses(
  values: FormValues,
  periode: Periode,
  tenantsById: Record<number, Tenant>,
  balanceQuittance: Record<
    number,
    { month: string; balance: number }[] | undefined
  >,
): TenantQuittanceStatuses {
  const months = computeMonthList(values, periode);
  const result: TenantQuittanceStatuses = {};
  values.tenantIds.forEach((tenantId) => {
    const tenant = tenantsById[tenantId];
    const balances = balanceQuittance[tenantId];
    const monthStatuses: Record<string, MonthStatus> = {};
    // Un seul document par période d'appel : les mois sélectionnés sont ramenés au mois
    // qui ouvre leur période (trimestre, semestre, année).
    getTenantPeriodMonths(tenant, months).forEach((month: string) => {
      const ajour = isMonthAjour(balances, month);
      const override = values.perTenantOverrides[overrideKey(tenantId, month)];
      monthStatuses[month] = {
        type: override ?? getMonthDefaultType(tenant, ajour),
        up_to_date: ajour,
      };
    });
    result[String(tenantId)] = monthStatuses;
  });
  return result;
}

function computeMonthList(values: FormValues, periode: Periode): string[] {
  if (periode === "date" && values.fromDate) {
    const start = moment(values.fromDate).startOf("month");
    const end = moment().startOf("month");
    const months: string[] = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, "month")) {
      months.push(cursor.format("MM/YYYY"));
      cursor.add(1, "month");
    }
    return months;
  }
  return [...values.selectedMonths].sort((a, b) =>
    moment(a, "MM/YYYY").diff(moment(b, "MM/YYYY")),
  );
}

interface PreviewPayload {
  tenantId: number;
  monthStatuses: Record<string, MonthStatus>;
}

async function fetchQuittancePreview(
  axios: AxiosInstance,
  { tenantId, monthStatuses }: PreviewPayload,
): Promise<string> {
  const response = await axios.post(
    "/tenants/quittance",
    {
      quittance_statuses: { [String(tenantId)]: monthStatuses },
      send_email: false,
    },
    { params: { preview: true } },
  );
  const previews: string[] = response.data?.previews ?? [];
  const errors: string[] = response.data?.errors ?? [];
  if (previews.length === 0) {
    const err = new Error(
      errors.length > 0
        ? errors.join(" ")
        : "Aucun document à prévisualiser pour cette période — les montants appelés sont à zéro.",
    );
    (err as Error & { displayMessage?: boolean }).displayMessage = true;
    throw err;
  }
  const blobResponse = await axios.get(previews[0], { responseType: "blob" });
  return URL.createObjectURL(blobResponse.data);
}

interface BulkQuittanceFieldsProps {
  tenants: Tenant[];
  tenantsById: Record<number, Tenant>;
  balanceQuittance: Record<
    number,
    { month: string; balance: number }[] | undefined
  >;
  isLoadingBalances: boolean;
  close: () => void;
  lockedTenantId?: number;
  periode: Periode;
  setPeriode: (periode: Periode) => void;
}

function BulkQuittanceFields({
  tenants,
  tenantsById,
  balanceQuittance,
  isLoadingBalances,
  close,
  lockedTenantId,
  periode,
  setPeriode,
}: BulkQuittanceFieldsProps) {
  const axios = useAuthenticatedAxios() as AxiosInstance;
  const { values, setFieldValue, status } = useFormikContext<FormValues>();
  const selectedMonths = useMemo(
    () => computeMonthList(values, periode),
    [values, periode],
  );

  const recentMonths = useMemo(() => buildRecentMonths(MAX_MONTHS), []);

  const { groupedBalances } = useAllTenantsBalances(false, {
    groupBy: { attr: "id_tenant", unique: true },
  }) as {
    groupedBalances: Record<number, { balance?: number } | undefined>;
  };

  const { groupedBalancesDashboard } = useBalancesDashboard({
    groupBy: { attr: "id_tenant", unique: true },
  }) as {
    groupedBalancesDashboard: Record<
      number,
      | { last_payments?: { date_transac: string; montant: number }[] }
      | undefined
    >;
  };

  const computedStatuses = useMemo(
    () =>
      buildQuittanceStatuses(values, periode, tenantsById, balanceQuittance),
    [values, periode, tenantsById, balanceQuittance],
  );

  const quittanceItems = useMemo<
    { tenantId: number; month: string; type: QuittanceType }[]
  >(() => {
    return values.tenantIds.flatMap((tenantId) => {
      const statuses = computedStatuses[String(tenantId)] ?? {};
      return Object.entries(statuses)
        .filter(([, s]) => s.type !== "none")
        .sort(([a], [b]) => moment(a, "MM/YYYY").diff(moment(b, "MM/YYYY")))
        .map(([month, s]) => ({ tenantId, month, type: s.type }));
    });
  }, [values.tenantIds, computedStatuses]);

  const amountsKey = useMemo(() => {
    if (quittanceItems.length === 0) return null;
    return quittanceItems
      .map((i) => `${i.tenantId}-${i.month}`)
      .sort()
      .join("|");
  }, [quittanceItems]);

  const { data: amountsData } = useSWR(
    amountsKey ? ["/tenants/quittance-amounts", amountsKey] : null,
    async () => {
      const response = await axios.post("/tenants/quittance-amounts", {
        items: quittanceItems.map((i) => ({
          tenant_id: i.tenantId,
          month: i.month,
        })),
      });
      return response.data as {
        amounts: { tenant_id: number; month: string; total: number | null }[];
      };
    },
  );

  const amountsByKey = useMemo<Record<string, number | null>>(() => {
    if (!amountsData) return {};
    const map: Record<string, number | null> = {};
    amountsData.amounts.forEach((a) => {
      map[`${a.tenant_id}-${a.month}`] = a.total;
    });
    return map;
  }, [amountsData]);

  const [activeItemKey, setActiveItemKey] = useState<string | null>(null);

  useEffect(() => {
    if (quittanceItems.length === 0) {
      if (activeItemKey !== null) setActiveItemKey(null);
      return;
    }
    const exists = quittanceItems.some(
      (item) => `${item.tenantId}-${item.month}` === activeItemKey,
    );
    if (!exists) {
      const first = quittanceItems[0];
      setActiveItemKey(`${first.tenantId}-${first.month}`);
    }
  }, [quittanceItems, activeItemKey]);

  const activeItem = useMemo(() => {
    if (activeItemKey === null) return null;
    return (
      quittanceItems.find(
        (item) => `${item.tenantId}-${item.month}` === activeItemKey,
      ) ?? null
    );
  }, [quittanceItems, activeItemKey]);

  const activePayloadKey =
    activeItem === null
      ? null
      : `${activeItem.tenantId}-${activeItem.month}-${activeItem.type}`;

  const activePayload = useMemo<PreviewPayload | null>(() => {
    if (activeItem === null) return null;
    const isAjour = isMonthAjour(
      balanceQuittance[activeItem.tenantId],
      activeItem.month,
    );
    return {
      tenantId: activeItem.tenantId,
      monthStatuses: {
        [activeItem.month]: { type: activeItem.type, up_to_date: isAjour },
      },
    };
  }, [activeItem, balanceQuittance]);

  const [view, setView] = useState<"config" | "preview">("config");

  // L'iframe PDF ne se charge que sur l'onglet visible : montée dans un panneau masqué, la
  // visionneuse du navigateur calcule son zoom sur une surface nulle et affiche une page minuscule.
  const previewEnabled =
    view === "preview" && activeItem !== null && !isLoadingBalances;

  const previewFetcher = useCallback(
    (payload: PreviewPayload) => fetchQuittancePreview(axios, payload),
    [axios],
  );

  const { previewUrl, previewLoading, previewError } =
    usePdfPreviewCache<PreviewPayload>({
      activeKey: activePayloadKey,
      activePayload,
      fetcher: previewFetcher,
      enabled: previewEnabled,
    });

  function applyPreset(preset: "previous" | "current" | "last3") {
    if (preset === "current") {
      setFieldValue("selectedMonths", [moment().format("MM/YYYY")]);
    } else if (preset === "previous") {
      setFieldValue("selectedMonths", [
        moment().subtract(1, "month").format("MM/YYYY"),
      ]);
    } else {
      setFieldValue(
        "selectedMonths",
        [0, 1, 2].map((i) => moment().subtract(i, "month").format("MM/YYYY")),
      );
    }
  }

  function addTenant(tenantId: number) {
    if (values.tenantIds.includes(tenantId)) return;
    setFieldValue("tenantIds", [...values.tenantIds, tenantId]);
  }

  function removeTenant(tenantId: number) {
    const nextIds = values.tenantIds.filter((id) => id !== tenantId);
    setFieldValue("tenantIds", nextIds);
    const nextOverrides = Object.fromEntries(
      Object.entries(values.perTenantOverrides).filter(
        ([key]) => !key.startsWith(`${tenantId}|`),
      ),
    );
    setFieldValue("perTenantOverrides", nextOverrides);
  }

  const tenantsOptions = useMemo(() => {
    return tenants
      .filter((t) => !values.tenantIds.includes(t.id))
      .filter((t) => t.status === TenantStatus.Actif.status)
      .map((t) => ({ value: t.id, label: t.nom }));
  }, [tenants, values.tenantIds]);

  const monthsValid = selectedMonths.length > 0;
  const tenantsValid = values.tenantIds.length > 0;

  return (
    <>
      <Tabs
        value={view}
        onValueChange={(v) => {
          if (v === "config" || v === "preview") setView(v);
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="config">Configuration</Tabs.Tab>
          <Tabs.Tab value="preview">Aperçu</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="config" keepMounted className={styles.tabsPanel}>
          <div className={styles.section}>
            <Text weight="bold">
              Pour quelles periodes voulez-vous générer les documents&nbsp;?
            </Text>
            {periode === "period" ? (
              <>
                <div className={styles.presetRow}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => applyPreset("previous")}
                  >
                    le mois précédent
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => applyPreset("current")}
                  >
                    le mois en cours
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => applyPreset("last3")}
                  >
                    3 derniers mois
                  </Button>
                </div>
                <div className={styles.monthGrid}>
                  {recentMonths.map((month) => {
                    const m = moment(month, "MM/YYYY");
                    return (
                      <Checkbox
                        key={month}
                        name="selectedMonths"
                        value={month}
                        className={styles.monthChip}
                      >
                        <Text as="span" weight="bold">
                          {m.format("MMM")}
                        </Text>
                        <Text as="span" color="muted">
                          {m.format("YYYY")}
                        </Text>
                      </Checkbox>
                    );
                  })}
                </div>
              </>
            ) : (
              <div>
                <Text>
                  Tous les mois entre cette date et aujourd'hui seront inclus.
                </Text>
                <TextInput
                  type="date"
                  name="fromDate"
                  marginForm=""
                  max={moment().format("YYYY-MM-DD")}
                />
              </div>
            )}
            <div>
              <Button
                type="button"
                variant="tertiary"
                onClick={() =>
                  setPeriode(periode === "period" ? "date" : "period")
                }
              >
                {periode === "period"
                  ? "Ou choisir à partir d'une date"
                  : "Ou choisir les mois manuellement"}
              </Button>
            </div>
          </div>

          <div className={styles.section}>
            <Text weight="bold">
              {lockedTenantId
                ? "Locataire concerné"
                : "Pour quels locataires souhaitez-vous générer un document ?"}
            </Text>

            {!lockedTenantId && (
              <ReactSelectStandalone
                label="Profil de locataire"
                options={tenantsOptions}
                value={null}
                onChange={(opt: { value: number } | null) => {
                  if (opt) addTenant(opt.value);
                }}
                placeholder="Sélectionner un locataire"
                menuPortalTarget={document.body}
                isClearable={false}
              />
            )}

            <div className={styles.tenantList}>
              {values.tenantIds.length === 0 && (
                <div className={styles.emptyState}>
                  <Text color="muted">Aucun locataire sélectionné.</Text>
                </div>
              )}
              {values.tenantIds.map((tenantId) => {
                const tenant = tenantsById[tenantId];
                if (!tenant) return null;
                return (
                  <TenantQuittanceCard
                    key={tenantId}
                    tenant={tenant}
                    selectedMonths={selectedMonths}
                    balances={balanceQuittance[tenantId]}
                    isLoadingBalances={isLoadingBalances}
                    onRemove={
                      lockedTenantId ? undefined : () => removeTenant(tenantId)
                    }
                  />
                );
              })}
            </div>
          </div>

          <Text>
            Qalimo ne re-génère pas les documents qui existent déjà. Pour les
            re-créer, supprimez d'abord ceux du locataire.
          </Text>

          <div className={bulkStyles.footer}>
            <Button variant="secondary" type="button" onClick={close}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={() => setView("preview")}
              disabled={!monthsValid || !tenantsValid}
            >
              Suivant
            </Button>
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="preview" keepMounted className={styles.tabsPanel}>
          {quittanceItems.length === 0 ? (
            <div className={styles.emptyState}>
              <Text color="muted">
                {values.tenantIds.length === 0
                  ? "Sélectionnez d'abord un locataire dans l'onglet Configuration."
                  : 'Aucun document à générer. Toutes les périodes sont sur "Ne rien générer".'}
              </Text>
            </div>
          ) : (
            <div className={bulkStyles.split}>
              <div className={bulkStyles.cardList}>
                {quittanceItems.map((item) => {
                  const tenant = tenantsById[item.tenantId];
                  if (!tenant) return null;
                  const key = `${item.tenantId}-${item.month}`;
                  // Titre sur le 1er mois de la période : un libellé de période complet déborde de la carte
                  const monthLabel = moment(item.month, "MM/YYYY").format(
                    "MMMM YYYY",
                  );
                  const fileLabel = `${getDocTypeFileLabel(
                    item.type,
                  )} ${monthLabel}.pdf`;
                  const fetchedTotal = amountsByKey[key];
                  const totalKnown = fetchedTotal !== undefined;
                  const tenantBalance = groupedBalances[item.tenantId]?.balance;
                  const lastPayment = getLastPaymentDate(
                    groupedBalancesDashboard[item.tenantId]?.last_payments,
                  );
                  return (
                    <BulkSelectionCard
                      key={key}
                      fileName={fileLabel}
                      tenantName={tenant.nom}
                      isActive={key === activeItemKey}
                      isIneligible={false}
                      onSelect={() => setActiveItemKey(key)}
                    >
                      <BulkSelectionCardRow label="Dernier paiement">
                        <Text as="span" weight="bold" color="muted">
                          {lastPayment ?? "—"}
                        </Text>
                      </BulkSelectionCardRow>
                      <BulkSelectionCardRow label="Total quittance">
                        <Text as="span" weight="bold" color="muted">
                          {!totalKnown
                            ? "…"
                            : fetchedTotal === null
                            ? "—"
                            : currencyFormatter(fetchedTotal)}
                        </Text>
                      </BulkSelectionCardRow>
                      <BulkSelectionCardRow label="Solde locataire">
                        <Text
                          as="span"
                          weight="bold"
                          color={
                            tenantBalance === undefined
                              ? "muted"
                              : tenantBalance >= 0
                              ? "success"
                              : "danger"
                          }
                        >
                          {tenantBalance === undefined
                            ? "—"
                            : currencyFormatter(tenantBalance)}
                        </Text>
                      </BulkSelectionCardRow>
                    </BulkSelectionCard>
                  );
                })}
              </div>

              <BulkPdfPreview
                iframeTitle="Aperçu du document généré"
                hasSelection={activeItem !== null}
                isIneligible={false}
                ineligibleTitle=""
                previewUrl={view === "preview" ? previewUrl : undefined}
                previewLoading={
                  previewLoading || (isLoadingBalances && activeItem !== null)
                }
                previewError={previewError}
                noSelectionMessage="Sélectionnez un document pour afficher l'aperçu."
              />
            </div>
          )}

          <Callout variant="danger">{status}</Callout>

          <div className={bulkStyles.footer}>
            <Checkbox name="sendEmail">
              Envoyer ces documents par email
            </Checkbox>
            <div className={bulkStyles.footerButtons}>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setView("config")}
              >
                Retour
              </Button>
              <FormikSubmitButton disabled={!monthsValid || !tenantsValid}>
                Générer
              </FormikSubmitButton>
            </div>
          </div>
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

interface TenantQuittanceCardProps {
  tenant: Tenant;
  selectedMonths: string[];
  balances?: { month: string; balance: number }[];
  isLoadingBalances: boolean;
  onRemove?: () => void;
}

function TenantQuittanceCard({
  tenant,
  selectedMonths,
  balances,
  isLoadingBalances,
  onRemove,
}: TenantQuittanceCardProps) {
  const { values } = useFormikContext<FormValues>();
  const problematicMonths = useMemo(() => {
    if (!balances) return [];
    return getTenantPeriodMonths(tenant, selectedMonths).filter((month) => {
      const balance = balances.find((b) => b.month === month);
      return balance !== undefined && balance.balance < 0;
    });
  }, [balances, selectedMonths, tenant]);
  const periodWord =
    (tenant.payment_recurrence ?? 1) > 1
      ? problematicMonths.length > 1
        ? "périodes"
        : "période"
      : "mois";

  const isAjour = problematicMonths.length === 0;
  const [manuallyCollapsed, setManuallyCollapsed] = useState(false);
  const hasOptions = problematicMonths.length > 0;

  const docTypeLabel = getTenantDocTypeLabel(tenant);

  return (
    <CollapsibleCard
      open={hasOptions && !manuallyCollapsed}
      onOpenChange={(open) => setManuallyCollapsed(!open)}
      title={
        <>
          <Avatar
            initials={getInitialsFromName(tenant.nom)}
            size="sm"
            variant="blue"
          />
          {tenant.nom}
        </>
      }
      headerActions={
        <>
          {isLoadingBalances ? (
            <Status color="info" text="Vérification…" />
          ) : isAjour ? (
            <Status color="success" text={`Loyer à jour · ${docTypeLabel}`} />
          ) : (
            <Status
              color="danger"
              text={`Loyer non à jour · ${problematicMonths.length} ${periodWord}`}
            />
          )}
          {onRemove && (
            <IconButton
              icon={IconTrash}
              variant="plain"
              size="sm"
              label="Retirer le locataire"
              onClick={onRemove}
            />
          )}
        </>
      }
    >
      {hasOptions && (
        <div className={styles.tenantCardBody}>
          <Text weight="bold">
            Sélectionnez une option pour chaque période non à jour.
          </Text>
          {problematicMonths.map((month) => {
            const currentType =
              values.perTenantOverrides[overrideKey(tenant.id, month)] ??
              getMonthDefaultType(tenant, false);
            const tenantDocType: QuittanceType =
              tenant.is_type_quittance === false
                ? "facture_loyer"
                : "quittance";
            const options: QuittanceType[] = ["recu", tenantDocType, "none"];
            return (
              <div key={month}>
                <Text as="span" variant="deci" weight="bold">
                  {getTenantPeriodLabel(tenant, month)}
                </Text>
                <RadioGroup styling="button" marginForm="">
                  {options.map((opt) => (
                    <Radio
                      key={opt}
                      name={`perTenantOverrides.${overrideKey(
                        tenant.id,
                        month,
                      )}`}
                      value={opt}
                      checked={currentType === opt}
                      label={getDocTypeLabel(opt)}
                    />
                  ))}
                </RadioGroup>
              </div>
            );
          })}
          <Callout variant="warning">
            Le loyer n'étant pas à jour, vous pouvez générer un reçu. La
            génération d'une {docTypeLabel.toLowerCase()} reste possible, mais
            elle est réservée aux loyers entièrement payés et peut engager votre
            responsabilité.
          </Callout>
        </div>
      )}
    </CollapsibleCard>
  );
}
